import { useImperativeHandle, useRef, useState } from 'react'

import { RedoOutlined, UndoOutlined } from '@ant-design/icons'
import { useMemoizedFn, useUpdateEffect } from 'ahooks'
import { Slider } from 'antd'
import { createStyles } from 'antd-style'
import Cropper from 'cropperjs'
import { noop } from 'lodash'

import { toArray } from '../../utils/array'
import { removeChildren } from '../../utils/dom'
import { DraggableModal } from '../DraggableModal'
import { DraggableModalRef } from '../DraggableModal/interface'

import { ImageCropperProps, ImageCropperStore } from './interface'
import { RotateButton } from './RotateButton'
import { getTemplate } from './utils/getTemplate'

const IMAGE_WIDTH = 9 * 80
const IMAGE_HEIGHT = 6 * 80

const useStyles = createStyles(({ token }) => ({
  modalWrapper: {
    textAlign: 'center'
  },
  modal: {
    display: 'inline-block',
    // https://github.com/fengyuanchen/cropperjs/issues/1074
    textAlign: 'initial'
  },
  container: {
    position: 'relative',
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
    [`& > cropper-canvas`]: {
      width: '100%',
      height: '100%'
    }
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    width: '60%',
    margin: '0 auto',
    paddingBlockStart: token.paddingXL,
    paddingBlockEnd: token.paddingMD
  },
  actionContent: {
    flex: 1
  }
}))

export const ImageCropper = ({
  ref,
  height,
  width,
  aspectRatio
}: ImageCropperProps) => {
  const modalRef = useRef<DraggableModalRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const cropperRef = useRef<Cropper>(undefined)

  const storeRef = useRef<ImageCropperStore>({
    resolve: noop,
    reject: noop
  })

  const widths = toArray(width)
  const heights = toArray(height)

  const selectionProps = {
    aspectRatio,
    resizable: widths.length > 1 || heights.length > 1,
    width: widths[0],
    height: heights[0],
    x: (IMAGE_WIDTH - widths[0]) / 2,
    y: (IMAGE_HEIGHT - heights[0]) / 2
  }

  const selectionAddListener = () => {
    const cropperSelection = cropperRef.current?.getCropperSelection()
    if (cropperSelection && selectionProps.resizable) {
      cropperSelection.onchange = (e: any) => {
        const { width, height } = e.detail
        let widthExceeded = false
        if (widths.length > 1) {
          widthExceeded = width < widths[0] || width > widths[1]
        }
        let heightExceeded = false
        if (heights.length > 1) {
          heightExceeded = height < heights[0] || height > heights[1]
        }

        return !(widthExceeded || heightExceeded)
      }
    }
  }

  const createCropper = useMemoizedFn((src: string) => {
    return new Promise<Blob>((resolve, reject) => {
      const image = new Image()
      image.src = src
      cropperRef.current = new Cropper(image, {
        container: containerRef.current as HTMLDivElement,
        template: getTemplate(selectionProps)
      })
      selectionAddListener()
      storeRef.current = {
        resolve,
        reject
      }
    })
  })

  const destroyCropper = () => {
    removeChildren(containerRef.current)
    cropperRef.current = undefined
  }

  const [angle, setAngle] = useState(0)
  const angleRef = useRef(angle)

  useUpdateEffect(() => {
    cropperRef.current
      ?.getCropperImage()
      ?.$rotate(`${angle - angleRef.current}deg`)
    angleRef.current = angle
  }, [angle])

  useImperativeHandle(
    ref,
    () => ({
      crop(src) {
        modalRef.current?.open()
        return createCropper(src)
      }
    }),
    [createCropper]
  )

  const handleModalOk = async () => {
    return cropperRef.current
      ?.getCropperSelection()
      ?.$toCanvas()
      .then(canvas => {
        canvas?.toBlob(blob => {
          if (blob) {
            storeRef.current.resolve(blob)
          } else {
            storeRef.current.reject()
          }
        })
      })
  }

  const { styles } = useStyles()

  return (
    <DraggableModal
      ref={modalRef}
      destroyOnHidden
      forceRender
      afterClose={destroyCropper}
      className={styles.modal}
      title='编辑图片'
      width='auto'
      wrapClassName={styles.modalWrapper}
      onOk={handleModalOk}
    >
      <div ref={containerRef} className={styles.container} />
      <div className={styles.actions}>
        <RotateButton
          icon={<UndoOutlined />}
          onRotate={() => {
            setAngle(Math.max(-180, angleRef.current - 5))
          }}
        />
        <Slider
          dots
          className={styles.actionContent}
          max={180}
          min={-180}
          value={angle}
          onChange={setAngle}
        />
        <RotateButton
          icon={<RedoOutlined />}
          onRotate={() => {
            setAngle(Math.min(180, angleRef.current + 5))
          }}
        />
      </div>
    </DraggableModal>
  )
}
